import { Fragment } from 'react';
import type { Role } from '../../types';
import {
  CAPABILITIES,
  ROLES_IN_ORDER,
  ROLE_LABELS,
  type Capability,
  type CapabilityArea,
} from '../../lib/permissions';

interface PermissionsTableProps {
  activeRole: Role;
}

const AREA_ORDER: CapabilityArea[] = [
  'Projects',
  'Evaluations',
  'Framework',
  'Capabilities',
  'Users',
  'System',
];

function groupByArea(): Record<CapabilityArea, Capability[]> {
  const out = {} as Record<CapabilityArea, Capability[]>;
  for (const area of AREA_ORDER) out[area] = [];
  for (const cap of CAPABILITIES) out[cap.area].push(cap);
  return out;
}

export function PermissionsTable({ activeRole }: PermissionsTableProps) {
  const grouped = groupByArea();

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="border-b border-wolf-700">
            <th scope="col" className="text-left py-2 pr-4 font-semibold text-wolf-100">
              Capability
            </th>
            {ROLES_IN_ORDER.map((role) => (
              <th
                key={role}
                scope="col"
                className={`text-center py-2 px-2 font-semibold ${
                  role === activeRole
                    ? 'text-action bg-wolf-800/60'
                    : 'text-wolf-300'
                }`}
              >
                {ROLE_LABELS[role]}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {AREA_ORDER.map((area) =>
            grouped[area].length === 0 ? null : (
              <Fragment key={area}>
                <tr className="bg-wolf-900/40">
                  <th
                    colSpan={1 + ROLES_IN_ORDER.length}
                    scope="colgroup"
                    className="text-left py-2 pr-4 text-xs uppercase tracking-wide text-wolf-400"
                  >
                    {area}
                  </th>
                </tr>
                {grouped[area].map((cap) => (
                  <tr key={cap.id} className="border-b border-wolf-800">
                    <th scope="row" className="text-left py-2 pr-4 font-normal">
                      <div className="text-wolf-100">{cap.label}</div>
                      <div className="text-xs text-wolf-400">{cap.description}</div>
                    </th>
                    {ROLES_IN_ORDER.map((role) => {
                      const has = cap.roles.includes(role);
                      return (
                        <td
                          key={role}
                          className={`text-center py-2 px-2 ${
                            role === activeRole ? 'bg-wolf-800/60' : ''
                          } ${has ? 'text-action' : 'text-wolf-600'}`}
                        >
                          {has ? '✓' : '—'}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </Fragment>
            ),
          )}
        </tbody>
      </table>
    </div>
  );
}
